// =====================================================================
// QuoteFormDialog (Financeiro F1 / S58) — cria orçamento do paciente.
// Paciente via Autocomplete (busca EHR) + itens do catálogo (clinic_services,
// read-only) com snapshot de preço + item avulso. Total recomputado no server.
// =====================================================================
import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  MenuItem,
  CircularProgress
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import DeleteIcon from "@material-ui/icons/Delete";
import AddIcon from "@material-ui/icons/Add";
import { toast } from "react-toastify";
import {
  listCatalog,
  createQuote,
  fmtMoney,
  PAYMENT_METHODS,
  financeErrorMessage
} from "../../services/financeService";
import { listPatients } from "../../services/patientService";
import useIsMobile from "../../utils/useIsMobile";

const QuoteFormDialog = ({ open, unitId, onClose, onSaved }) => {
  const isMobile = useIsMobile();
  const keyRef = useRef(1);

  const [patient, setPatient] = useState(null);
  const [patientOptions, setPatientOptions] = useState([]);
  const [patientLoading, setPatientLoading] = useState(false);

  const [catalog, setCatalog] = useState([]);
  const [catalogValue, setCatalogValue] = useState(null);

  const [items, setItems] = useState([]);
  const [discount, setDiscount] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [expectedMethod, setExpectedMethod] = useState(""); // [S91] forma prevista
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset + carrega catálogo ao abrir
  useEffect(() => {
    if (!open) return;
    setPatient(null);
    setPatientOptions([]);
    setItems([]);
    setDiscount("");
    setValidUntil("");
    setExpectedMethod("");
    setNotes("");
    setCatalogValue(null);
    (async () => {
      try {
        const data = await listCatalog();
        setCatalog(Array.isArray(data?.services) ? data.services : []);
      } catch (e) {
        setCatalog([]);
      }
    })();
  }, [open]);

  // Busca de paciente com debounce
  useEffect(() => {
    return () => {};
  }, []);

  const debounceRef = useRef(null);
  const handlePatientInput = (term) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!term || term.trim().length < 2) {
      setPatientOptions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setPatientLoading(true);
      try {
        const data = await listPatients({ search: term, pageSize: 10 });
        setPatientOptions(Array.isArray(data?.patients) ? data.patients : []);
      } catch (e) {
        setPatientOptions([]);
      } finally {
        setPatientLoading(false);
      }
    }, 400);
  };

  const addItem = (it) => setItems((prev) => [...prev, { key: keyRef.current++, ...it }]);

  const addCatalogItem = (svc) => {
    if (!svc) return;
    addItem({
      serviceId: svc.id,
      description: svc.name,
      qty: 1,
      unitPrice: Number(svc.price) || 0
    });
    setCatalogValue(null);
  };

  const addCustomItem = () =>
    addItem({ serviceId: null, description: "", qty: 1, unitPrice: 0 });

  const updateItem = (key, field, value) =>
    setItems((prev) => prev.map((it) => (it.key === key ? { ...it, [field]: value } : it)));

  const removeItem = (key) => setItems((prev) => prev.filter((it) => it.key !== key));

  const subtotal = items.reduce(
    (s, it) => s + (Number(it.qty) || 0) * (Number(it.unitPrice) || 0),
    0
  );
  const total = Math.max(subtotal - (Number(String(discount).replace(",", ".")) || 0), 0);

  const handleSave = async () => {
    if (!patient) {
      toast.error("Selecione o paciente.");
      return;
    }
    const cleanItems = items
      .map((it) => ({
        serviceId: it.serviceId || null,
        description: String(it.description || "").trim(),
        qty: Math.max(parseInt(it.qty, 10) || 1, 1),
        unitPrice: Number(String(it.unitPrice).replace(",", ".")) || 0
      }))
      .filter((it) => it.description);
    if (!cleanItems.length) {
      toast.error("Adicione ao menos um item (com descrição).");
      return;
    }
    setSaving(true);
    try {
      await createQuote({
        patientId: patient.id,
        items: cleanItems,
        discount: Number(String(discount).replace(",", ".")) || 0,
        validUntil: validUntil || undefined,
        expectedPaymentMethod: expectedMethod || undefined, // [S91] forma prevista
        notes: notes || undefined,
        // [S90] unidade ativa (modelo B); as parcelas herdam no approve.
        unitId: unitId != null ? unitId : undefined
      });
      toast.success("Orçamento criado.");
      onSaved && onSaved();
    } catch (err) {
      toast.error(financeErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>Novo orçamento</DialogTitle>
      <DialogContent>
        <Autocomplete
          options={patientOptions}
          value={patient}
          loading={patientLoading}
          getOptionLabel={(o) => (o && o.fullName) || ""}
          getOptionSelected={(o, v) => o.id === (v && v.id)}
          onChange={(e, v) => setPatient(v)}
          onInputChange={(e, v) => handlePatientInput(v)}
          noOptionsText="Digite ao menos 2 letras…"
          renderInput={(params) => (
            <TextField
              {...params}
              label="Paciente"
              margin="dense"
              variant="outlined"
              placeholder="Buscar por nome / CPF / telefone"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {patientLoading ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
        />

        <Box mt={2} mb={1} display="flex" gap={1} alignItems="center" style={{ gap: 8 }}>
          <Autocomplete
            style={{ flexGrow: 1 }}
            options={catalog}
            value={catalogValue}
            getOptionLabel={(o) =>
              o ? `${o.name}${o.price ? ` — ${fmtMoney(o.price)}` : o.priceText ? ` — ${o.priceText}` : ""}` : ""
            }
            getOptionSelected={(o, v) => o.id === (v && v.id)}
            onChange={(e, v) => addCatalogItem(v)}
            renderInput={(params) => (
              <TextField {...params} label="Adicionar do catálogo" margin="dense" variant="outlined" />
            )}
          />
          <Button startIcon={<AddIcon />} onClick={addCustomItem}>Item avulso</Button>
        </Box>

        {items.length > 0 && (
          <div style={{ overflowX: "auto", width: "100%" }}>
          <Table size="small" style={{ minWidth: 640 }}>
            <TableHead>
              <TableRow>
                <TableCell>Descrição</TableCell>
                <TableCell align="center" style={{ width: 80 }}>Qtd</TableCell>
                <TableCell align="right" style={{ width: 130 }}>Valor unit.</TableCell>
                <TableCell align="right" style={{ width: 120 }}>Total</TableCell>
                <TableCell style={{ width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.key}>
                  <TableCell>
                    <TextField
                      value={it.description}
                      onChange={(e) => updateItem(it.key, "description", e.target.value)}
                      fullWidth
                      placeholder="Descrição do item"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      type="number"
                      value={it.qty}
                      onChange={(e) => updateItem(it.key, "qty", e.target.value)}
                      inputProps={{ min: 1, style: { textAlign: "center" } }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      value={it.unitPrice}
                      onChange={(e) => updateItem(it.key, "unitPrice", e.target.value)}
                      inputProps={{ step: "0.01", min: "0", style: { textAlign: "right" } }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {fmtMoney((Number(it.qty) || 0) * (Number(it.unitPrice) || 0))}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => removeItem(it.key)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        )}

        <Box mt={2} display="flex" style={{ gap: 16, flexWrap: "wrap" }}>
          <TextField
            label="Desconto (R$)"
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            type="number"
            inputProps={{ step: "0.01", min: "0" }}
            style={{ flexGrow: 1, minWidth: 130 }}
          />
          <TextField
            label="Validade"
            type="date"
            value={validUntil}
            onChange={(e) => setValidUntil(e.target.value)}
            InputLabelProps={{ shrink: true }}
            style={{ flexGrow: 1, minWidth: 150 }}
          />
          <TextField
            select
            label="Forma de pagamento prevista"
            value={expectedMethod}
            onChange={(e) => setExpectedMethod(e.target.value)}
            style={{ flexGrow: 1, minWidth: 130 }}
            helperText="Opcional — o método real é registrado na baixa"
          >
            <MenuItem value=""><em>Não informar</em></MenuItem>
            {PAYMENT_METHODS.map((m) => (
              <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
            ))}
          </TextField>
          <Box flexGrow={1} />
          <Box textAlign="right">
            <Typography variant="caption" color="textSecondary">Subtotal {fmtMoney(subtotal)}</Typography>
            <Typography variant="h6">Total {fmtMoney(total)}</Typography>
          </Box>
        </Box>

        <TextField
          label="Observações (opcional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          margin="dense"
          multiline
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          Salvar orçamento
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuoteFormDialog;
